import { useState, useCallback, useEffect } from 'preact/hooks';
import { OddballProvider, useOddball, SynthMode } from './midi-context';
import { setMasterVolume } from './audio/audio-engine';
import { CURATED_INSTRUMENTS } from './audio/soundfont-player';
import { SensorDashboard } from './tools/sensor-dashboard';
import { GestureLog } from './tools/gesture-log';
import { PentatonicToy } from './tools/pentatonic-toy';
import { ThrowArp } from './tools/throw-arp';
import { RollTexture } from './tools/roll-texture';
import { OrientationPad } from './tools/orientation-pad';
import { RhythmMapper } from './tools/rhythm-mapper';
import { NatureSoundscape } from './tools/nature-soundscape';
import { AxisExplorer } from './tools/axis-explorer';
import { MoodDrift } from './tools/mood-drift';
import { RollMelody } from './tools/roll-melody';
import { RollCalibrate } from './tools/roll-calibrate';

const TABS = [
  { id: 'dashboard', label: 'Sensors', component: SensorDashboard },
  { id: 'gestures', label: 'Gestures', component: GestureLog },
  { id: 'pentatonic', label: 'Pentatonic', component: PentatonicToy },
  { id: 'throw', label: '\u2605 Throw Arp', component: ThrowArp },
  { id: 'roll', label: 'Roll Texture', component: RollTexture },
  { id: 'melody', label: '\u2605 Floor Roll', component: RollMelody },
  { id: 'xypad', label: 'XY Pad', component: OrientationPad },
  { id: 'rhythm', label: 'Rhythm', component: RhythmMapper },
  { id: 'nature', label: 'Nature', component: NatureSoundscape },
  { id: 'axis', label: 'Axis', component: AxisExplorer },
  { id: 'mood', label: 'Mood', component: MoodDrift },
  { id: 'calibrate', label: 'Calibrate', component: RollCalibrate },
] as const;

function ConnectionToggle() {
  const { connected, connectionMethod, setConnectionMethod, bleAvailable } = useOddball();
  if (connected) return null;

  const seg = (label: string, method: 'webmidi' | 'webbluetooth', disabled: boolean) => (
    <button
      onClick={() => !disabled && setConnectionMethod(method)}
      disabled={disabled}
      title={disabled ? 'Requires Chrome or a Chromium-based browser' : undefined}
      style={{
        padding: '3px 10px',
        border: 'none',
        borderRadius: '10px',
        background: connectionMethod === method ? '#333' : 'transparent',
        color: disabled ? '#444' : connectionMethod === method ? '#fff' : '#888',
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontSize: '0.7rem',
        fontWeight: connectionMethod === method ? 600 : 400,
      }}
    >
      {label}
    </button>
  );

  return (
    <div
      style={{
        display: 'inline-flex',
        borderRadius: '12px',
        border: '1px solid #333',
        padding: '2px',
        gap: '2px',
      }}
    >
      {seg('MIDI', 'webmidi', false)}
      {seg('BLE', 'webbluetooth', !bleAvailable)}
    </div>
  );
}

function useNarrow(breakpoint = 768) {
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < breakpoint
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const onChange = () => setNarrow(mq.matches);
    mq.addEventListener('change', onChange);
    setNarrow(mq.matches);
    return () => mq.removeEventListener('change', onChange);
  }, [breakpoint]);
  return narrow;
}

function AppShell() {
  const { connected, status, connect, disconnect, speedModifier, setSpeedModifier, octaveOffset, setOctaveOffset, autoIdleDisconnect, setAutoIdleDisconnect, bleAvailable, synthMode, setSynthMode, gmProgram, setGmProgram, instrumentLoading } = useOddball();
  const [activeTab, setActiveTab] = useState(TABS[0].id);
  const [volume, setVolume] = useState(80);
  const [speedSlider, setSpeedSlider] = useState(0); // log2 scale: -200..200 → 0.25x..4x
  const [menuOpen, setMenuOpen] = useState(false);
  const narrow = useNarrow();

  const onVolumeChange = useCallback((e: Event) => {
    const v = Number((e.target as HTMLInputElement).value);
    setVolume(v);
    setMasterVolume(v / 100);
  }, []);

  const onSpeedChange = useCallback((e: Event) => {
    const raw = Number((e.target as HTMLInputElement).value);
    setSpeedSlider(raw);
    setSpeedModifier(Math.pow(2, raw / 100));
  }, [setSpeedModifier]);

  const resetSpeed = useCallback(() => {
    setSpeedSlider(0);
    setSpeedModifier(1.0);
  }, [setSpeedModifier]);

  const onOctaveChange = useCallback((e: Event) => {
    setOctaveOffset(Number((e.target as HTMLInputElement).value));
  }, [setOctaveOffset]);

  const resetOctave = useCallback(() => {
    setOctaveOffset(0);
  }, [setOctaveOffset]);

  const ActiveComponent = TABS.find((t) => t.id === activeTab)!.component;

  const tabButton = (tab: typeof TABS[number], onClick: () => void, style?: Record<string, unknown>) => (
    <button
      key={tab.id}
      onClick={onClick}
      style={{
        padding: '6px 12px',
        borderRadius: '6px',
        border: 'none',
        background: activeTab === tab.id ? '#333' : 'transparent',
        color: activeTab === tab.id ? '#fff' : '#888',
        cursor: 'pointer',
        fontSize: '0.8rem',
        whiteSpace: 'nowrap',
        fontWeight: activeTab === tab.id ? 600 : 400,
        ...style,
      }}
    >
      {tab.label}
    </button>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* Top bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '8px',
          padding: '8px 12px',
          borderBottom: '1px solid #222',
          background: '#111',
          flexShrink: 0,
        }}
      >
        {/* Connection controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          <span
            style={{
              display: 'inline-block',
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: connected ? '#4a7' : '#555',
            }}
          />
          <ConnectionToggle />
          <button
            onClick={connected ? disconnect : connect}
            className={connected ? '' : 'pulse-btn'}
            style={{
              padding: '6px 18px',
              borderRadius: '14px',
              border: '1px solid ' + (connected ? '#4a7' : '#6a6'),
              background: connected ? 'transparent' : '#1a2e1f',
              color: connected ? '#4a7' : '#cfc',
              cursor: 'pointer',
              fontSize: '0.85rem',
              fontWeight: connected ? 400 : 600,
              animation: connected ? 'none' : 'pulse 1.5s ease-in-out infinite',
            }}
          >
            {connected ? 'Disconnect' : 'Connect'}
          </button>
        </div>

        {narrow ? (
          /* Hamburger menu button + Sources link for narrow viewports */
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <a
              href="oddballplayground.tar.gz"
              download="oddballplayground.tar.gz"
              style={{
                fontSize: '0.7rem',
                color: '#555',
                textDecoration: 'none',
              }}
            >
              Sources
            </a>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              style={{
                padding: '4px 8px',
                borderRadius: '6px',
                border: '1px solid #333',
                background: menuOpen ? '#333' : 'transparent',
                color: '#888',
                cursor: 'pointer',
                fontSize: '1rem',
                lineHeight: 1,
              }}
            >
              {menuOpen ? '\u2715' : '\u2630'}
            </button>
          </div>
        ) : (
          /* Inline tab bar for wide viewports */
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '4px',
              flex: 1,
            }}
          >
            {TABS.map((tab) => tabButton(tab, () => setActiveTab(tab.id)))}
          </div>
        )}
        {!narrow && (
          <a
            href="oddballplayground.tar.gz"
            download="oddballplayground.tar.gz"
            style={{
              fontSize: '0.7rem',
              color: '#555',
              textDecoration: 'none',
              flexShrink: 0,
            }}
          >
            Sources
          </a>
        )}
      </div>

      {/* Dropdown menu for narrow viewports */}
      {narrow && menuOpen && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '4px',
            padding: '8px 12px',
            background: '#111',
            borderBottom: '1px solid #222',
            flexShrink: 0,
          }}
        >
          {TABS.map((tab) =>
            tabButton(tab, () => { setActiveTab(tab.id); setMenuOpen(false); }, { padding: '8px 14px' })
          )}
        </div>
      )}

      {/* Global controls: volume, speed, octave */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          padding: '4px 12px',
          background: '#0d0d0d',
          borderBottom: '1px solid #1a1a1a',
          flexShrink: 0,
          flexWrap: 'wrap',
        }}
      >
        {!bleAvailable && (
          <span style={{ fontSize: '0.8rem', color: '#a74', fontWeight: 700, whiteSpace: 'nowrap' }}>
            Web Bluetooth requires Chrome or a Chromium-based browser
          </span>
        )}

        {/* Volume */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '0.65rem', color: '#555', whiteSpace: 'nowrap' }}>Vol</span>
          <input
            type="range"
            min="0"
            max="100"
            value={volume}
            onInput={onVolumeChange}
            style={{ width: '80px', accentColor: '#666', height: '4px' }}
          />
          <span style={{ fontSize: '0.65rem', color: '#555', fontFamily: 'monospace', minWidth: '24px' }}>{volume}</span>
        </div>

        {/* Speed */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '0.65rem', color: '#555', whiteSpace: 'nowrap' }}>Speed</span>
          <input
            type="range"
            min="-200"
            max="200"
            value={speedSlider}
            onInput={onSpeedChange}
            style={{ width: '80px', accentColor: speedSlider === 0 ? '#666' : '#a74', height: '4px' }}
          />
          <span style={{ fontSize: '0.65rem', color: speedSlider === 0 ? '#555' : '#a74', fontFamily: 'monospace', minWidth: '32px' }}>
            {speedModifier.toFixed(1)}x
          </span>
          {speedSlider !== 0 && (
            <button
              onClick={resetSpeed}
              style={{
                padding: '0 4px', border: '1px solid #444', borderRadius: 4,
                background: 'transparent', color: '#888', cursor: 'pointer',
                fontSize: '0.6rem', lineHeight: '14px',
              }}
            >
              1x
            </button>
          )}
        </div>

        {/* Octave */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '0.65rem', color: '#555', whiteSpace: 'nowrap' }}>Oct</span>
          <input
            type="range"
            min="-2"
            max="2"
            step="1"
            value={octaveOffset}
            onInput={onOctaveChange}
            style={{ width: '60px', accentColor: octaveOffset === 0 ? '#666' : '#5ae', height: '4px' }}
          />
          <span style={{ fontSize: '0.65rem', color: octaveOffset === 0 ? '#555' : '#5ae', fontFamily: 'monospace', minWidth: '20px' }}>
            {octaveOffset > 0 ? '+' : ''}{octaveOffset}
          </span>
          {octaveOffset !== 0 && (
            <button
              onClick={resetOctave}
              style={{
                padding: '0 4px', border: '1px solid #444', borderRadius: 4,
                background: 'transparent', color: '#888', cursor: 'pointer',
                fontSize: '0.6rem', lineHeight: '14px',
              }}
            >
              0
            </button>
          )}
        </div>

        {/* Sound mode */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{
            display: 'inline-flex',
            borderRadius: '10px',
            border: '1px solid #333',
            padding: '2px',
            gap: '2px',
          }}>
            {(['oscillator', 'soundfont'] as SynthMode[]).map((mode) => (
              <button
                key={mode}
                onClick={() => setSynthMode(mode)}
                style={{
                  padding: '3px 10px',
                  border: 'none',
                  borderRadius: '8px',
                  background: synthMode === mode ? '#333' : 'transparent',
                  color: synthMode === mode ? '#fff' : '#888',
                  cursor: 'pointer',
                  fontSize: '0.65rem',
                  fontWeight: synthMode === mode ? 600 : 400,
                }}
              >
                {mode === 'oscillator' ? 'Synth' : 'SoundFont'}
              </button>
            ))}
          </div>
          {synthMode === 'soundfont' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <select
                value={gmProgram}
                onChange={(e) => setGmProgram(Number((e.target as HTMLSelectElement).value))}
                style={{
                  background: '#1a1a1a',
                  color: '#aaa',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  padding: '2px 4px',
                  fontSize: '0.65rem',
                  cursor: 'pointer',
                }}
              >
                {CURATED_INSTRUMENTS.map((inst) => (
                  <option key={inst.program} value={inst.program}>{inst.name}</option>
                ))}
              </select>
              {instrumentLoading && (
                <span style={{ fontSize: '0.6rem', color: '#a74' }}>Loading...</span>
              )}
            </div>
          )}
        </div>

        {/* Idle disconnect */}
        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={autoIdleDisconnect}
            onChange={(e) => setAutoIdleDisconnect((e.target as HTMLInputElement).checked)}
            style={{ accentColor: '#666' }}
          />
          <span style={{ fontSize: '0.65rem', color: '#555', whiteSpace: 'nowrap' }}>Idle disconnect (2 min)</span>
        </label>

      </div>

      {/* Active tab */}
      <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
        <ActiveComponent />
      </div>

      {/* Footer */}
      <div
        style={{
          padding: '6px 12px',
          borderTop: '1px solid #222',
          fontSize: '0.7rem',
          color: '#555',
          textAlign: 'center',
          flexShrink: 0,
        }}
      >
        {status}
      </div>
    </div>
  );
}

export function App() {
  return (
    <OddballProvider>
      <AppShell />
    </OddballProvider>
  );
}
